import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Row, Col, Select, Typography, Spin, message } from 'antd';
import { PhoneOutlined, MailOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEmployeeTypesRequest } from '../../../redux/actions/employeeActions';
import { fetchProvinceTypesRequest } from '../../../redux/actions/provinceActions';
import { fetchSiteRequest } from '../../../redux/actions/siteActions';
import axiosInstance from '../../../interceptor/interceptor';

const { Option } = Select;
const { Title } = Typography;

const EmployeeForm = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user'));

  const [formData, setFormData] = useState({
    empno: '',
    password: '',
    emptypeid: '',
    empsiteid: user?.role === "SUPERADMIN" ? '' : user.empsiteid,  
    firstname: '',
    middlename: '',
    lastname: '',
    address: '',
    city: '',
    provinceid: '',
    postalcode: '',
    phone: '',
    email: '',
    empsitename: '', // The site name is not displayed in the form, but we store it for submission
  });
  const [errors, setErrors] = useState({});
  console.log(errors);

  const { employeeTypes, provinces, sites } = useSelector(state => ({
    employeeTypes: state.employeeType.employeeTypes,
    provinces: state.provinceType.provinceTypes,
    sites: state.site.data || [],
  }));

  useEffect(() => {
    const fetchData = async () => {
      dispatch(fetchEmployeeTypesRequest());
      dispatch(fetchProvinceTypesRequest());
      dispatch(fetchSiteRequest());
      setLoading(false);
    };

    fetchData();
  }, [dispatch]);

  useEffect(() => {
    form.setFieldsValue(formData);
  }, [formData, form]);

  const validate = () => {
    const validationErrors = {};
    if (!formData.empno) validationErrors.empno = 'Employee number is required';
    if (!formData.password) validationErrors.password = 'Password is required';
    if (!formData.emptypeid) validationErrors.emptypeid = 'Employee type is required';
    if (!formData.firstname) validationErrors.firstname = 'First name is required';
    if (!formData.lastname) validationErrors.lastname = 'Last name is required';
    if (!formData.phone) validationErrors.phone = 'Phone number is required';
    if (!formData.email) validationErrors.email = 'Email is required';
    return validationErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      try {
        const formDataToSubmit = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
          formDataToSubmit.append(key, value);
        });

        await axiosInstance.post('/auth/register', formDataToSubmit);

        message.success('Employee created successfully.');

        setFormData({
          empno: '',
          password: '',
          emptypeid: '',
          empsiteid: user?.role === "SUPERADMIN" ? '' : user.empsiteid,
          firstname: '',
          middlename: '',
          lastname: '',
          address: '',
          city: '',
          provinceid: '',
          postalcode: '',
          phone: '',
          email: '',
          empsitename: '',
        });
        setErrors({});

      } catch (error) {
        if (error.response) {
          if (error.response.data.message.includes("Duplicate entry")) {
            message.error('This Employee Number (empno) already exists.');
            form.setFields([
              {
                name: 'empno',
                errors: ['Employee Number already exists. Please choose a different one.'],
              },
            ]);
          } else {
            message.error('Error submitting form: ' + error.response.data.message);
          }
        } else {
          message.error('An error occurred while submitting the form.');
        }
      }
    } else {
      message.error('Please correct the errors in the form.');
    }
  };

  if (loading) {
    return <Spin tip="Loading..." />;
  }


  return (
    <div className='formclass'>
      <Title level={3} className="form-title">
        Employee's Registration
      </Title>

      <Form
        form={form}
        name="employee_form"
        className='mt-4'
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={formData} // Use the formData for initial values
      >
        {/* Row 1: empno, password, emptypeid */}
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="empno"
              label="Employee Number"
              rules={[{ required: true, message: 'Please input employee number!' }]} >
              <Input value={formData.empno} onChange={(e) => setFormData({ ...formData, empno: e.target.value })} />
            </Form.Item>
          </Col>
          {
            user?.role === "SUPERADMIN" && (
          <Col xs={24} sm={12} md={8}>
          
              <Form.Item name="empsitename" label="Site ">
              <Select
                value={formData.empsitename} // Use site name for display
                onChange={(value) => {
                  const selectedSite = sites.find(site => site.id === value);
                  setFormData({ ...formData, empsitename: selectedSite?.sitename || '', empsiteid: value });
                }}
                placeholder="Select Site"
                 showSearch // Enables search
                 optionFilterProp="children" // Searches in dropdown text
                 filterOption={(input, option) =>
                   (option?.children || '').toLowerCase().includes(input.toLowerCase())
                 }
              >
                {sites && sites.map((site) => (
                  <Option key={site.id} value={site.id}>{site.sitename}</Option>
                ))}
              </Select>
            </Form.Item>
          
        
                      </Col>
  )}

        
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="emptypeid"
              label="Employee Type"
              rules={[{ required: true, message: 'Please select employee type!' }]} >
              <Select
                value={formData.emptypeid}
                onChange={(value) => setFormData({ ...formData, emptypeid: value })}
                placeholder="Select Employee Type"
                showSearch 
                       optionFilterProp="children" 
                       filterOption={(input, option) =>
                         (option?.children || '').toLowerCase().includes(input.toLowerCase())
                       }
              >
                {employeeTypes && employeeTypes.map((type) => (
                  <Option key={type.id} value={type.id}>
                    {type.emptype}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Row 2: firstname, lastname, phone */}
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="firstname"
              label="First Name"
              rules={[{ required: true, message: 'Please input first name!' }]} >
              <Input value={formData.firstname} onChange={(e) => setFormData({ ...formData, firstname: e.target.value })} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="middlename"
              label="Middle Name"
              >
              <Input value={formData.middlename} onChange={(e) => setFormData({ ...formData, middlename: e.target.value })} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="lastname"
              label="Last Name"
              rules={[{ required: true, message: 'Please input last name!' }]} >
              <Input value={formData.lastname} onChange={(e) => setFormData({ ...formData, lastname: e.target.value })} />
            </Form.Item>
          </Col>

        </Row>

        
        {/* Row 3: Site Name field */}
        <Row gutter={16}>
        <Col xs={24} sm={12} md={8}>
            <Form.Item name="city" label="City">
              <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
            </Form.Item>
          </Col>


                      <Col xs={24} sm={12} md={8}>
            <Form.Item name="address" label="Address">
              <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
             <Form.Item
               name="provinceid"
               label="Province"
               rules={[{ required: true, message: 'Please input province!' }]} >
               <Select
                 value={formData.provinceid}
                 onChange={(value) => setFormData({ ...formData, provinceid: value })}
                 placeholder="Select Province"
                 dropdownStyle={{ maxHeight: 230 }}
                 listHeight={210}
                 getPopupContainer={triggerNode => triggerNode.parentNode}
                 showSearch // Enables search
          // dropdownAlign={{offset: [0,4]}}
                 optionFilterProp="children" // Searches in dropdown text
                 filterOption={(input, option) =>
                   (option?.children || '').toLowerCase().includes(input.toLowerCase())
                 }
               >
                 {provinces && provinces.map((province) => (
                   <Option key={province.id} value={province.id}>
                     {province.province}
                   </Option>
                 ))}
               </Select>
             </Form.Item>
          </Col>
        </Row>

        {/* Row 4: email */}
        <Row gutter={16}>
        <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="phone"
              label="Phone"
              rules={[{ required: true, message: 'Please input phone number!' }]} >
              <Input
                prefix={<PhoneOutlined />}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true, message: 'Please input email!' }]} >
              <Input
                prefix={<MailOutlined />}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Please input password!' }]} >
              <Input.Password value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} />
            </Form.Item>
          </Col>

        </Row>


        <Button type="primary" htmlType="submit" className="mt-4" 
         style={{ width: '200px', margin: '0 auto', display: 'block', lineHeight: '5px', }}>
          Submit
        </Button>
      </Form>
    </div>
  );
};

export default EmployeeForm;
